import {
  Color,
  colors,
  RADIUS,
  SHADOW,
  Turnstile,
  withAlpha,
  WithHalfdot,
  Wordmark,
} from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import { useLocale } from "~/i18n";
import { ACCOUNT_DELETION_PATH } from "~/lib/account-deletion";
import { requestOtp } from "~/lib/api";
import { KEYBOARD_INSET_VAR } from "~/lib/keyboard-inset";
import { legalPath } from "~/lib/legal";
import { returnToForPath } from "~/lib/return-to";
import { turnstileBridgeUrl } from "~/lib/turnstile";
import { useAuthStore } from "~/store/auth-store";
import type { MembershipInviteStatus } from "~/types/membership-invite";

import {
  AUTH_CODE_PATH,
  type CodeStepState,
  translateAuthError,
} from "./auth-flow";
import { InviteInstallActions } from "./InviteInstallActions";

/**
 * Step one of passwordless sign-in: the e-mail address the code will go to.
 *
 * Laid out as a drawer — brand footage fills the top of the screen and the form rides
 * over it on a sheet whose action is docked to the bottom edge, under the thumb. Step
 * two is a screen of its own (`VerifyScreen`), pushed onto history: entering a code has
 * nothing to do with the pitch, and the member can come back here.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Served from `public/`, so it is inside the Capacitor bundle and plays offline. */
const VIDEO_SRC = "/video-elyxeer.mp4";
/** First frame of the same file: what fills the top until the video has decoded. */
const VIDEO_POSTER = "/video-elyxeer-poster.jpg";

/**
 * How much of a phone screen the footage keeps. The sheet is taller than the rest,
 * so it laps over the bottom of the video — that overlap is what reads as a drawer.
 */
const VIDEO_HEIGHT = "52dvh";
const SHEET_HEIGHT = "54dvh";

/**
 * The looping brand footage behind the sheet.
 *
 * `muted` has to be set on the element as well as in JSX: autoplay is only permitted
 * for a muted video, and React drops the attribute on the initial render often enough
 * that WebKit sees an unmuted video and refuses to start it. The scrim above it is not
 * decoration — the wordmark sits on the footage, whose brightness we do not control.
 */
function BrandVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    // Safari can still reject the play() promise (low power mode); the poster stays.
    void video.play().catch(() => undefined);
  }, []);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        // On desktop the sheet floats as a card, so the footage takes the whole page.
        height: { xs: VIDEO_HEIGHT, md: "100%" },
        overflow: "hidden",
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        poster={VIDEO_POSTER}
        src={VIDEO_SRC}
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          // The crop: the footage is portrait (9:16) and neither slot is, so
          // `cover` fills the width and takes a band out of the middle — most of
          // it on desktop, where the slot is the whole page.
          objectFit: "cover",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: {
            xs: `linear-gradient(180deg, ${withAlpha(colors[Color.Black900], 0.45)} 0%, ${withAlpha(
              colors[Color.Black900],
              0,
            )} 45%)`,
            md: withAlpha(colors[Color.Black900], 0.35),
          },
        }}
      />
    </Box>
  );
}

interface LoginScreenProps {
  initialEmail?: string;
  emailReadOnly?: boolean;
  returnTo?: string;
  inviteToken?: string;
  inviteStatus?: MembershipInviteStatus;
}

export function LoginScreen({
  initialEmail = "",
  emailReadOnly = false,
  returnTo,
  inviteToken,
  inviteStatus,
}: LoginScreenProps = {}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const config = useAuthStore((state) => state.config);

  const [email, setEmail] = useState(initialEmail);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  // Bumping this remounts the widget, which is the only way to get a new token —
  // Turnstile tokens are single-use and the previous one is spent by the request.
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const captchaRequired = config?.captcha_required ?? false;

  const handleCaptcha = useCallback(
    (token: string | null) => setCaptchaToken(token),
    [],
  );

  const submitEmail = async (event: FormEvent) => {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email.trim())) {
      setErrorKey("auth.errors.invalidEmail");
      return;
    }

    setBusy(true);
    setErrorKey(null);
    try {
      const response = await requestOtp(email.trim(), {
        captchaToken: captchaToken ?? undefined,
        locale,
      });
      const state: CodeStepState = {
        email: email.trim(),
        signingUp: response.is_new_user,
        debugCode: response.debug_code,
        // An explicit prop wins; otherwise the path the member was actually trying to
        // reach carries through sign-in, so a webinar reminder tapped on an expired
        // session still lands on the webinar.
        returnTo: returnTo ?? returnToForPath(pathname),
      };
      navigate(AUTH_CODE_PATH, { state });
    } catch (error) {
      setErrorKey(translateAuthError(error));
    } finally {
      setBusy(false);
      // The token was consumed by the request above, whether or not it succeeded.
      if (captchaRequired) {
        setCaptchaToken(null);
        setCaptchaNonce((nonce) => nonce + 1);
      }
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100dvh",
        // Whatever the sheet does not cover on a phone is video, and on desktop the
        // card floats over it — but a slow first frame must not flash white.
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",
        // Only sideways: the footage clips itself, and clipping the column as well
        // would put anything that does not fit — on a short phone with the keyboard
        // up — somewhere the member cannot scroll to.
        overflowX: "hidden",
        // Everything in flow is laid out inside what the keyboard leaves; the footage
        // is positioned against the padding box's top edge, so it does not move.
        paddingBottom: `var(${KEYBOARD_INSET_VAR}, 0px)`,
      }}
    >
      <BrandVideo />

      {/* The lockup rides on the footage, so it is fixed light rather than themed. */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          paddingInline: 3,
          // Rendered outside the app shell, so it carries its own safe-area inset.
          paddingTop: "calc(28px + env(safe-area-inset-top))",
        }}
      >
        <Wordmark size={26} color={Color.Silver100} markColor={Color.White} />
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          // Phone: a drawer pinned to the bottom edge. Desktop: a card, centred.
          marginTop: "auto",
          marginBottom: { md: "auto" },
          marginInline: { md: "auto" },
          maxWidth: { md: 460 },
          // The drawer's height is a floor, not a promise: with the keyboard up there
          // is not 54dvh to spare, and holding out for it would push the button off
          // the bottom of what is left. It gives way by exactly what the keys took.
          minHeight: {
            xs: `max(0px, calc(${SHEET_HEIGHT} - var(${KEYBOARD_INSET_VAR}, 0px)))`,
            md: 0,
          },
          backgroundColor: "background.paper",
          borderRadius: {
            xs: `${RADIUS.xl}px ${RADIUS.xl}px 0 0`,
            md: `${RADIUS.xl}px`,
          },
          boxShadow: SHADOW.raised,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          padding: 3,
          paddingTop: 4,
          paddingBottom: {
            xs: "calc(24px + env(safe-area-inset-bottom))",
            md: 3,
          },
        }}
      >
        <Box
          component="form"
          onSubmit={submitEmail}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            // Fills the sheet so the action below can dock to its floor.
            flex: 1,
          }}
        >
          <Box>
            <Typography variant="H1" sx={{ color: "text.primary" }}>
              <WithHalfdot size={12}>{t("auth.emailTitle")}</WithHalfdot>
            </Typography>
            <Typography
              variant="T2"
              sx={{ color: "text.secondary", marginTop: 1.5 }}
            >
              {t("auth.emailSubtitle")}
            </Typography>
          </Box>

          {pathname === ACCOUNT_DELETION_PATH ? (
            <Alert severity="info" data-testid="account.delete-sign-in">
              {t("profile.deleteAccount.signInNotice")}
            </Alert>
          ) : null}

          {inviteToken ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Alert severity="info" data-testid="invite.login-notice">
                {t(
                  inviteStatus === "claimed"
                    ? "invite.claimedSignInNotice"
                    : "invite.pendingSignInNotice",
                  { email: initialEmail },
                )}
              </Alert>
              <InviteInstallActions token={inviteToken} />
            </Box>
          ) : null}

          <TextField
            label={t("auth.emailLabel")}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            autoFocus={!emailReadOnly}
            required
            fullWidth
            slotProps={{
              // Still required — the asterisk is just noise on a lone field.
              inputLabel: { required: false },
              htmlInput: {
                "data-testid": "auth.email",
                inputMode: "email",
                readOnly: emailReadOnly,
              },
            }}
          />

          {captchaRequired ? (
            <Turnstile
              key={captchaNonce}
              onToken={handleCaptcha}
              bridgeUrl={turnstileBridgeUrl()}
            />
          ) : null}

          {errorKey ? <Alert severity="error">{t(errorKey)}</Alert> : null}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy || (captchaRequired && !captchaToken)}
            data-testid="auth.continue"
            sx={{ marginTop: "auto" }}
          >
            {t("common.continue")}
          </Button>

          {/*
            The documents, reachable before anything is typed. Deliberately not the
            acceptance notice — that belongs on the code screen, under the button that
            does the accepting — so this is a plain pair of links, which is also what
            an app store reviewer looks for on the first screen.
          */}
          <Typography
            variant="T3"
            sx={{ color: "text.muted", textAlign: "center" }}
            data-testid="auth.legal-links"
          >
            <Link
              component={RouterLink}
              to={legalPath("terms")}
              underline="always"
              sx={{ color: "inherit" }}
            >
              {t("legal.menu.terms")}
            </Link>
            {" · "}
            <Link
              component={RouterLink}
              to={legalPath("privacy")}
              underline="always"
              sx={{ color: "inherit" }}
            >
              {t("legal.menu.privacy")}
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
